import React, { useState, useMemo } from "react";
import { Box, Typography, Alert, useMediaQuery, Card, CardContent } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import { createBarValueLabelsLayer } from "./shared/BarValueLabelsLayer";
import { useCompanyDividends, isDividendNotFoundError } from "../hooks/useCompanyDividends";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { getChartColors } from "../constants/chartColors";
import {
  ChartHeader,
  GrowthMetricsBar,
  GrowthMetric,
} from "./shared";
import ChartTooltip from "./shared/ChartTooltip";
import {
  getFlatPortfolioColors,
} from "../../../../utils/getFlatPortfolioColors";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";

interface DividendYieldChartProps {
  ticker: string;
  price: number;
  height?: number;
  isModal?: boolean;
  onOpenModal?: () => void;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
}

interface ChartDataPoint {
  period: string;
  yield: number;
  annualDividend: number;
  payments: number;
  [key: string]: string | number;
}

const DividendYieldChart: React.FC<DividendYieldChartProps> = ({
  ticker,
  price,
  height = 400,
  isModal = false,
  onOpenModal,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const colors = getFlatPortfolioColors(theme);
  const { formatPrice } = useStockCurrency();

  // Fetch dividend history
  const { data: dividends, isLoading, error } = useCompanyDividends(ticker, 100);
  const chartColors = getChartColors(theme.palette.mode === "dark");

  const showValueLabels = !isMobile;
  const valueLabelsLayer = useMemo(
    () =>
      createBarValueLabelsLayer({
        yAxisFormat: (value) => `${value.toFixed(1)}%`,
        groupMode: "grouped",
        barKeys: ["yield"],
        textColor: colors.text.primary,
        isModal,
      }),
    [colors.text.primary, isModal]
  );

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!dividends || dividends.length === 0 || price <= 0) return [];

    // Filter out special dividends, invalid payment dates, and only include positive amounts
    const regularDividends = dividends.filter(
      (d) => d.frequency !== "Special" && d.dividend > 0 && d.paymentDate && dayjs(d.paymentDate).isValid()
    );

    if (regularDividends.length === 0) return [];

    // Group dividends by calendar year
    const yearlyData: Record<string, { totalDividend: number; payments: number }> = {};

    regularDividends.forEach((d) => {
      const year = dayjs(d.paymentDate).format("YYYY");
      if (!yearlyData[year]) {
        yearlyData[year] = { totalDividend: 0, payments: 0 };
      }
      yearlyData[year].totalDividend += d.dividend;
      yearlyData[year].payments += 1;
    });

    // Show last 10 years, sorted chronologically
    // Exclude current year as it's incomplete data
    const tenYearsAgo = dayjs().subtract(10, "years").format("YYYY");
    const currentYear = dayjs().format("YYYY");

    return Object.entries(yearlyData)
      .filter(([year]) => year >= tenYearsAgo && year !== currentYear)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => ({
        period: year,
        yield: (data.totalDividend / price) * 100,
        annualDividend: data.totalDividend,
        payments: data.payments,
      }));
  }, [dividends, price]);

  // Calculate year-over-year yield change metrics
  const growthMetrics = useMemo((): GrowthMetric[] => {
    // chartData already excludes current year
    if (chartData.length < 2) return [];

    const yoyChanges: number[] = [];
    for (let i = 1; i < chartData.length; i++) {
      const prevYield = chartData[i - 1].yield;
      const currYield = chartData[i].yield;
      if (prevYield > 0) {
        const change = ((currYield - prevYield) / prevYield) * 100;
        yoyChanges.push(change);
      }
    }

    if (yoyChanges.length === 0) return [];

    const metrics: GrowthMetric[] = [];

    // Most recent YoY change (1Y)
    const lastYoYChange = yoyChanges[yoyChanges.length - 1];
    metrics.push({ label: "YoY Change (1Y)", value: lastYoYChange, stacked: true });

    // 3-year average if enough data
    if (yoyChanges.length >= 3) {
      const last3Years = yoyChanges.slice(-3);
      const avg3Y = last3Years.reduce((a, b) => a + b, 0) / last3Years.length;
      metrics.push({ label: "Avg YoY Change (3Y)", value: avg3Y, stacked: true });
    }

    // 5-year average if enough data
    if (yoyChanges.length >= 5) {
      const last5Years = yoyChanges.slice(-5);
      const avg5Y = last5Years.reduce((a, b) => a + b, 0) / last5Years.length;
      metrics.push({ label: "Avg YoY Change (5Y)", value: avg5Y, stacked: true });
    }

    return metrics;
  }, [chartData]);

  const handleOpenModal = onOpenModal ?? (() => setModalOpen(true));
  const handleCloseModal = onCloseModal ?? (() => setModalOpen(false));

  // Check if company has yield data
  const hasYieldData = chartData.length > 0 && chartData.some((d) => d.yield > 0);
  const isEmpty = !chartData || chartData.length === 0 || !hasYieldData;

  // Wrapper for card rendering with consistent styling
  const wrapInCard = (content: React.ReactNode) => {
    if (!renderCard) return <>{content}</>;
    return (
      <Card
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 0,
          ...(isModal && { height: "100%", display: "flex", flexDirection: "column" }),
        }}
      >
        <CardContent
          sx={{
            ...(isModal && {
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }),
          }}
        >
          {content}
        </CardContent>
      </Card>
    );
  };

  // Loading state
  if (isLoading) {
    return wrapInCard(
      <>
        <ChartHeader
          title="Annual Dividend Yield"
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height}
        >
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </>
    );
  }

  // Special message for non-dividend paying companies (empty data or 404)
  const isNoDividendData = isEmpty || isDividendNotFoundError(error);
  if (!isLoading && isNoDividendData) {
    return wrapInCard(
      <>
        <ChartHeader
          title="Annual Dividend Yield"
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: 2,
          }}
        >
          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: "0.875rem",
              textAlign: "center",
            }}
          >
            This company does not currently pay dividends, or dividend data could not be loaded.
          </Typography>
        </Box>
      </>
    );
  }

  // Error state (non-404 errors)
  if (error) {
    return wrapInCard(
      <>
        <ChartHeader
          title="Annual Dividend Yield"
          showFullscreen={false}
          isModal={isModal}
          onCloseModal={handleCloseModal}
        />
        <Alert severity="error" sx={{ mt: 1 }}>
          Unable to load dividend data.
        </Alert>
      </>
    );
  }

  const chartContent = (
    <>
      <ChartHeader
        title="Annual Dividend Yield"
        showFullscreen={showFullscreen}
        isModal={isModal}
        onOpenModal={handleOpenModal}
        onCloseModal={handleCloseModal}
      />

      <Box
        sx={{
          height: height,
          ...(isModal && { flex: 1, minHeight: 0 }),
        }}
      >
        <ResponsiveBar
          data={chartData}
          keys={["yield"]}
          indexBy="period"
          margin={{
            top: isMobile ? 10 : 30,
            right: isMobile ? 10 : 20,
            bottom: growthMetrics.length > 0 ? (isMobile ? 50 : 60) : (isMobile ? 35 : 40),
            left: isMobile ? 55 : 70,
          }}
          padding={0.3}
          valueScale={{ type: "linear", min: 0, max: "auto" }}
          indexScale={{ type: "band", round: true }}
          colors={chartColors.positive}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: isMobile ? 10 : 8,
            tickRotation: -45,
            tickValues: (() => {
              // Show fewer labels on mobile, and space them out for readability
              const maxLabels = isMobile ? 5 : Math.min(chartData.length, 12);
              if (chartData.length <= maxLabels) return undefined;
              return chartData
                .filter((_, i) => i % Math.ceil(chartData.length / maxLabels) === 0)
                .map((d) => d.period);
            })(),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: isMobile ? 10 : 8,
            tickRotation: 0,
            legend: isMobile ? undefined : "Yield (%)",
            legendPosition: "middle",
            legendOffset: -50,
            format: (value) => `${(value as number).toFixed(1)}%`,
            tickValues: isMobile ? 4 : 5,
          }}
          enableLabel={false}
          enableGridY={true}
          gridYValues={isMobile ? 4 : 5}
          layers={[
            "grid",
            "axes",
            "bars",
            ...(showValueLabels ? [valueLabelsLayer] : []),
            "markers",
            "legends",
            "annotations",
          ]}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 10 : 12,
              fill: colors.text.primary,
            },
            axis: {
              domain: { line: { stroke: "transparent", strokeWidth: 0 } },
              legend: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: colors.text.primary,
                  fontWeight: 600,
                },
              },
              ticks: {
                line: { stroke: "transparent", strokeWidth: 0 },
                text: {
                  fontSize: isMobile ? 10 : 11,
                  fill: colors.text.secondary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: colors.border.primary,
                strokeWidth: 1,
                strokeOpacity: 0.3,
              },
            },
            tooltip: {
              container: {
                background: colors.background.secondary,
                color: colors.text.primary,
                fontSize: 12,
                borderRadius: 0,
                boxShadow: "none",
                border: `1px solid ${colors.border.primary}`,
              },
            },
          }}
          tooltip={({ indexValue, value, data, color }) => {
            const chartPoint = data as ChartDataPoint;
            const rows = [
              { label: "Annual Yield", value: `${(value as number).toFixed(2)}%` },
              { label: "Annual Dividend", value: formatPrice(chartPoint.annualDividend, 2) },
            ];
            return (
              <ChartTooltipPortal>
                <ChartTooltip header={String(indexValue)} headerColor={color} rows={rows} />
              </ChartTooltipPortal>
            );
          }}
        />
      </Box>

      <GrowthMetricsBar metrics={growthMetrics} />
    </>
  );

  const modalContent = (
    <DividendYieldChart
      ticker={ticker}
      price={price}
      height={700}
      isModal={true}
      renderCard={false}
      onCloseModal={handleCloseModal}
    />
  );

  return (
    <>
      {wrapInCard(chartContent)}
      {/* Modal for fullscreen view */}
      {modalOpen && (
        <Box
          onClick={handleCloseModal}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "90vw",
              height: "90vh",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}
          >
            {modalContent}
          </Box>
        </Box>
      )}
    </>
  );
};

export default DividendYieldChart;
